import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import AiPlayground from './ai-playground/AiPlayground';
import ProtectedRoute from './components/protected-route/ProtectedRoute';
import { PROJECT_TYPES, PROJECT_VARIANTS, USER_ROLES } from './constants';
import Customers from './customers/Customers';
import GenieGo from './genie-go/GenieGo';
import Genie from './genie/Genie';
import Id8 from './id8/Id8';
import Layout from './layout/Layout';
import EditProject from './projects/edit-project/EditProject';
import ProjectCreate from './projects/project-create/ProjectCreate';
import SonarCollectData from './projects/project-create/sonar/collect-data/SonarCollectData';
import ProjectsList from './projects/projects-list/ProjectsList';
import { checkAuth } from './redux/security/securityActions';
import Resonance from './resonance/Resonance';
import ScoutCollectData from './scout-collect-data/ScoutCollectData';
import Scout from './scout-new/Scout';
import SonarDashboard from './sonar/Dashboard';
import CreateUser from './user-management/CreateUser/CreateUser';
import EditUser from './user-management/EditUser/EditUser';
import Users from './user-management/Users/Users';
import { ProjectProvider } from './context/ProjectContext';
import Settings from './settings/Settings';
import ProjectRequest from './project-request/ProjectRequest';
import Activ8 from './activ8/Activ8';

import 'react-tooltip/dist/react-tooltip.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import './font/inter/inter.css';
import './scss/index.scss';

function App() {
  const dispatch = useDispatch();

  const isLoadingUserStatus = useSelector((state) => state.securityNew.isLoadingUserStatus);

  useEffect(() => {
    dispatch(checkAuth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoadingUserStatus) {
    return null;
  }

  return (
    <Layout>
      <Routes>
        <Route exact path='/projects' element={<ProjectsList />} />
        <Route
          path='/customers/*'
          exact
          element={
            <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
              <Customers />
            </ProtectedRoute>
          }
        />
        <Route
          path='/projects/create/*'
          exact
          element={
            <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN} redirectPath='/projects'>
              <ProjectCreate />
            </ProtectedRoute>
          }
        />
        <Route
          path='/projects/edit/*'
          exact
          element={
            <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
              <EditProject />
            </ProtectedRoute>
          }
        />
        <Route
          path='/users'
          exact
          element={
            <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path='/users/edit/:id'
          exact
          element={
            <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
              <EditUser />
            </ProtectedRoute>
          }
        />
        <Route
          path='/users/create'
          exact
          element={
            <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
              <CreateUser />
            </ProtectedRoute>
          }
        />
        <Route exact path='/project/scout/:id' element={<Scout />} />
        <Route exact path='/project/sonar/:id' element={<SonarDashboard />} />
        <Route
          exact
          path='/project/resonance/:id'
          element={
            <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.RESONANCE}>
              <Resonance />
            </ProjectProvider>
          }
        />
        <Route
          exact
          path='/project/genie/:id'
          element={
            <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.GENIE}>
              <Genie />
            </ProjectProvider>
          }
        />
        <Route exact path='/project/genie-go/:id' element={<GenieGo />} />
        <Route
          exact
          path='/project/id8/:id'
          element={
            <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.ID8}>
              <Id8 />
            </ProjectProvider>
          }
        />
        <Route path='/project/sonar/collect-data/:id' exact element={<SonarCollectData />} />
        <Route exact path='/project/scout/collect-data/:id' element={<ScoutCollectData />} />
        <Route exact path='/playground' element={<AiPlayground />} />
        <Route
          exact
          path='/project/activ8/:id'
          element={
            <ProjectProvider projectVariant={PROJECT_VARIANTS.ADMIN} projectType={PROJECT_TYPES.ACTIV8}>
              <Activ8 />
            </ProjectProvider>
          }
        />

        <Route
          path='/settings/*'
          exact
          element={
            <ProtectedRoute permissions={USER_ROLES.GLOBAL_ADMIN}>
              <Settings />
            </ProtectedRoute>
          }
        />
        <Route path='/project-request' exact element={<ProjectRequest />} />
        <Route path='*' element={<Navigate to={'/projects'} />} />
      </Routes>
    </Layout>
  );
}

export default App;
